// SRT loss as three numbers (решение пользователя 24.09), the one reading
// every screen shows - inputs, legs, the output wall, statistics:
//
//   detected    SRTStats.loss_pct_10s - packets that did not arrive the first
//               time ("линк болеет"; alarm srt_loss);
//   recovered   what retransmission brought back in time: detected minus
//               unrecovered;
//   unrecovered SRTStats.lost_pct_10s - packets that never arrived in time,
//               holes in the picture or the sound ("потери в эфире"; alarm
//               srt_unrecovered).
//
// All three are percent of the packets of the last 10 s, so detected =
// recovered + unrecovered. Control always sends lost_pct_10s, 0 included
// (domain.SRTStats, not omitempty - SRTALARM review); an absent field (a
// control older than SRTALARM) leaves only "detected" known and the other two
// null (shown as a dash), never a made-up zero.
//
// A sender (a leg) can drop packets without a NAK first (TLPKTDROP on a
// congested link), so its unrecovered count may exceed its detected one;
// "detected" is shown as at least "unrecovered" to keep the identity above -
// every unrecovered packet was, after all, a loss.

import type { SRTStats } from '@/api/types'
import { defineMessages } from '@/shared/i18n'
import type { Tone } from './tone'

/**
 * Below this bitrate a loss percentage is a ratio over a near-empty
 * denominator (a leg carrying only PSI packets): meaningless, so it is not
 * shown. The same number as LOW_TRAFFIC_BPS_THRESHOLD of features/channel and
 * app/layout/summary.ts (shared/ cannot import them).
 */
export const SRT_LOSS_MIN_BPS = 100_000

/** The product defaults of the two SRT alarms (domain/alarm_defaults.go), for the tone only. */
export const DETECTED_WARN_PCT = 2
export const UNRECOVERED_ALARM_PCT = 0.1

export interface SrtLossTriple {
  detected: number
  /** null: lost_pct_10s absent (a control older than SRTALARM). */
  recovered: number | null
  unrecovered: number | null
}

/** The three numbers of one SRT sample; null when there is no sample or its traffic is too small to mean anything. */
export function srtLossTriple(srt: SRTStats | null | undefined, bitrateBps?: number): SrtLossTriple | null {
  if (!srt) return null
  if (bitrateBps !== undefined && bitrateBps < SRT_LOSS_MIN_BPS) return null
  const reported = Math.max(0, srt.loss_pct_10s)
  if (srt.lost_pct_10s === undefined) return { detected: reported, recovered: null, unrecovered: null }
  const unrecovered = Math.max(0, srt.lost_pct_10s)
  const detected = Math.max(reported, unrecovered)
  return { detected, recovered: detected - unrecovered, unrecovered }
}

/**
 * How loud the triple looks: unrecovered loss above the srt_unrecovered
 * default is an outage (alarm), any unrecovered loss or detected loss above
 * the srt_loss default is a sick link (warn), otherwise ok.
 */
export function srtLossTone(l: SrtLossTriple | null): Tone {
  if (!l) return 'idle'
  const u = l.unrecovered ?? 0
  if (u > UNRECOVERED_ALARM_PCT) return 'alarm'
  if (u > 0 || l.detected > DETECTED_WARN_PCT) return 'warn'
  return 'ok'
}

/**
 * A percentage with a decimal point in both languages: 0 reads "0", tiny
 * values keep two decimals (the outage threshold is 0.1 %, so "0.0" would
 * hide it), the rest one.
 */
export function formatLossPct(v: number | null): string {
  if (v === null) return '—'
  if (v === 0) return '0 %'
  if (v < 0.01) return '<0.01 %'
  if (v < 1) return `${v.toFixed(2)} %`
  return `${v.toFixed(1)} %`
}

export const srtLossMessages = defineMessages({
  ru: {
    detected: 'обнаружено',
    recovered: 'восстановлено',
    unrecovered: 'не восстановлено',
    title: 'Потери SRT за 10 с',
    hint:
      'Потери SRT за последние 10 с, в % от всех пакетов. Обнаружено — пакеты не пришли с первого раза (линк болеет). ' +
      'Восстановлено — SRT успел переслать их вовремя, картинка цела. Не восстановлено — потеряно по-настоящему: ' +
      'рассыпания в картинке или звуке (аларм «Потери в эфире»).',
    noData: 'нет данных о потерях',
    lowTraffic: 'слишком мало трафика, чтобы считать потери',
    triple: 'обнаружено {detected}, восстановлено {recovered}, не восстановлено {unrecovered}',
    summary: 'потери SRT: {triple}',
  },
  en: {
    detected: 'detected',
    recovered: 'recovered',
    unrecovered: 'not recovered',
    title: 'SRT loss over 10 s',
    hint:
      'SRT loss over the last 10 s, in % of all packets. Detected: packets that did not arrive the first time (the link is sick). ' +
      'Recovered: SRT re-sent them in time, the picture is intact. Not recovered: really lost, ' +
      'the picture or the sound breaks up (alarm “Loss on air”).',
    noData: 'no loss data',
    lowTraffic: 'too little traffic to count loss',
    triple: 'detected {detected}, recovered {recovered}, not recovered {unrecovered}',
    summary: 'SRT loss: {triple}',
  },
})

/** The three numbers as one line: "обнаружено 3.0 %, восстановлено 2.5 %, не восстановлено 0.50 %"; a dash without a reading. */
export function formatLossTriple(l: SrtLossTriple | null): string {
  if (!l) return '—'
  return srtLossMessages.t('triple', {
    detected: formatLossPct(l.detected),
    recovered: formatLossPct(l.recovered),
    unrecovered: formatLossPct(l.unrecovered),
  })
}

/** One line of text for a place that has no room for the component (a tile's info line, a tooltip). */
export function srtLossText(l: SrtLossTriple | null): string {
  if (!l) return srtLossMessages.t('noData')
  return srtLossMessages.t('summary', { triple: formatLossTriple(l) })
}
